/**
 * API keys - enter a provider key, test it against the live service, save it.
 *
 * The key is written by the API into apps/api/.env and takes effect on the
 * next request, with no restart. It travels one way: typed here, sent once,
 * and never returned - the server sends back a mask (`••••••••ab12`) and a
 * verdict, so a key cannot be read back out of the browser.
 *
 * Testing and saving are separate deliberately. A key can be trialled before
 * it is committed to disk, and a key that fails because Companies House is
 * down can still be saved.
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiError, settingsApi } from '../lib/api.js';
import {
  IconAlert,
  IconCheck,
  IconEye,
  IconEyeOff,
  IconInfo,
  IconKey,
  IconPlug,
  IconTrash,
} from '../components/Icons.jsx';

/** Maps a test verdict onto the alert style that carries the right weight. */
const RESULT_STYLES = {
  OK: { className: 'alert-ok', title: 'Connection successful', Icon: IconCheck },
  INVALID_KEY: { className: 'alert-error', title: 'Key rejected', Icon: IconAlert },
  FORBIDDEN: { className: 'alert-error', title: 'Access forbidden', Icon: IconAlert },
  // Not an error: a 429 proves the key authenticated. Warn, so it does not
  // read as "your key is broken" when the key is fine.
  RATE_LIMITED: { className: 'alert-warn', title: 'Rate limited', Icon: IconInfo },
  UPSTREAM_ERROR: { className: 'alert-warn', title: 'Companies House is having trouble', Icon: IconInfo },
  UNREACHABLE: { className: 'alert-error', title: 'Could not reach Companies House', Icon: IconAlert },
  NO_KEY: { className: 'alert-info', title: 'Nothing to test', Icon: IconInfo },
};

/** "3 minutes ago" - a test result is only meaningful relative to now. */
function timeAgo(iso) {
  if (!iso) return null;
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleString();
}

function TestResultAlert({ result, testedStoredKey }) {
  const style = RESULT_STYLES[result.code] ?? RESULT_STYLES.UPSTREAM_ERROR;
  const { Icon } = style;

  return (
    <div className={`alert ${style.className}`} role="status">
      <span className="alert-icon">
        <Icon />
      </span>
      <span className="alert-body">
        <span className="alert-title">
          {style.title}
          {!testedStoredKey && result.code !== 'NO_KEY' && (
            <span className="badge" style={{ marginLeft: 'var(--sp-2)' }}>
              unsaved key
            </span>
          )}
        </span>
        {result.message}
        <span className="text-xs text-faint" style={{ display: 'block', marginTop: 'var(--sp-1)' }}>
          Checked {timeAgo(result.checkedAt)}
          {result.latencyMs != null && ` · ${result.latencyMs} ms round trip`}
        </span>
      </span>
    </div>
  );
}

/** Confirmation before destroying a credential - a misclick here costs a re-registration. */
function RemoveDialog({ onCancel, onConfirm, busy }) {
  // Escape closes it, as every dialog should. Not while the delete is in
  // flight: dismissing the frame would not stop the request behind it.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [busy, onCancel]);

  return (
    <div className="scrim" onClick={busy ? undefined : onCancel} role="presentation">
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="remove-key-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="dialog-header">
          <div>
            <h2 className="dialog-title" id="remove-key-title">
              Remove the Companies House key?
            </h2>
            <p className="dialog-description">
              The key is deleted from <code>apps/api/.env</code> and the integration is switched
              off. Acquisition stops working until a key is entered again. Your existing company
              data is not affected.
            </p>
          </div>
        </div>
        <div className="dialog-footer">
          <span className="dialog-footer-spacer" />
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={onConfirm} disabled={busy}>
            {busy && <span className="spinner" />}
            {busy ? 'Removing' : 'Remove key'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ApiKeysPage() {
  const [settings, setSettings] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [keyInput, setKeyInput] = useState('');
  const [revealed, setRevealed] = useState(false);

  const [busy, setBusy] = useState(null); // 'saving' | 'testing' | 'toggling' | 'removing'
  const [actionError, setActionError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const provider = settings?.providers?.find((item) => item.id === 'companies-house') ?? null;

  useEffect(() => {
    let cancelled = false;

    settingsApi
      .apiKeys()
      .then((data) => {
        if (!cancelled) setSettings(data);
      })
      .catch((caught) => {
        if (!cancelled) setLoadError(caught.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * One wrapper for every mutating action.
   *
   * Each endpoint returns the whole settings payload, so state is replaced
   * rather than patched - the form can never show something the server does
   * not agree with.
   */
  const run = useCallback(async (label, action) => {
    setBusy(label);
    setActionError(null);
    setNotice(null);

    try {
      return await action();
    } catch (caught) {
      setActionError(
        caught instanceof ApiError
          ? caught.status === 429
            ? 'Too many attempts in a short window. Wait a few minutes and try again.'
            : caught.message
          : 'Something went wrong. Check the API log for details.',
      );
      return null;
    } finally {
      setBusy(null);
    }
  }, []);

  const typedKey = keyInput.trim();

  async function handleTest() {
    // With something typed, the typed value is what the user means to test -
    // even if a different key is already saved.
    const outcome = await run('testing', () => settingsApi.testCompaniesHouse(typedKey || undefined));
    if (!outcome) return;

    setTestResult({ result: outcome.result, testedStoredKey: outcome.testedStoredKey });
    setSettings(outcome.settings);
  }

  async function handleSave(event) {
    event.preventDefault();
    if (!typedKey) return;

    const saved = await run('saving', () => settingsApi.saveCompaniesHouse({ apiKey: typedKey }));
    if (!saved) return;

    setSettings(saved);
    setKeyInput('');
    setRevealed(false);

    // Immediately verify what was just written, so "saved" and "works" are
    // never two separate things the user has to remember to check.
    const outcome = await run('testing', () => settingsApi.testCompaniesHouse());
    if (outcome) {
      setTestResult({ result: outcome.result, testedStoredKey: true });
      setSettings(outcome.settings);
      setNotice(
        outcome.result.ok
          ? 'Key saved to apps/api/.env and verified. No restart needed.'
          : 'Key saved to apps/api/.env, but the test did not pass - see below.',
      );
    } else {
      setNotice('Key saved to apps/api/.env. No restart needed.');
    }
  }

  async function handleToggleEnabled(enabled) {
    const saved = await run('toggling', () => settingsApi.saveCompaniesHouse({ enabled }));
    if (!saved) return;
    setSettings(saved);
    setNotice(
      enabled
        ? 'Companies House integration enabled.'
        : 'Companies House integration disabled. The key is still saved.',
    );
  }

  async function handleRemove() {
    const saved = await run('removing', () => settingsApi.removeCompaniesHouse());
    if (!saved) return;
    setSettings(saved);
    setConfirmRemove(false);
    setTestResult(null);
    setKeyInput('');
    setNotice('Key removed from apps/api/.env.');
  }

  if (loading) {
    return (
      <div className="page">
        <div className="loading-panel">
          <span className="spinner spinner-lg" />
          Loading API keys
        </div>
      </div>
    );
  }

  const lastTest = testResult ?? (provider?.lastTest ? { result: provider.lastTest, testedStoredKey: true } : null);
  const anyBusy = busy !== null;
  const readOnly = settings ? !settings.writable : false;

  return (
    <div className="page">
      <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">API keys</h1>
          <p className="page-description">
            Keys entered here are written to <code>apps/api/.env</code> on the server and take
            effect immediately — no restart. A key is never sent back to the browser: once saved,
            only its last four characters are shown.
          </p>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-error" style={{ marginBottom: 'var(--sp-5)' }}>
          <span className="alert-icon">
            <IconAlert />
          </span>
          <span className="alert-body">
            <span className="alert-title">Could not load your API keys</span>
            {loadError}
          </span>
        </div>
      )}

      {readOnly && (
        <div className="alert alert-warn" style={{ marginBottom: 'var(--sp-5)' }}>
          <span className="alert-icon">
            <IconAlert />
          </span>
          <span className="alert-body">
            <span className="alert-title">No environment file to write to</span>
            The API cannot find <code>{settings?.envFilePath}</code>. Copy{' '}
            <code>apps/api/.env.example</code> to <code>apps/api/.env</code> and restart the API
            before saving a key here.
          </span>
        </div>
      )}

      {provider && (
        <div className="col" style={{ gap: 'var(--sp-4)' }}>
          <div className="card">
            <div className="card-header">
              <span className="card-title row row-tight">
                <span className="provider-mark">
                  <IconKey size={14} />
                </span>
                {provider.label}
              </span>
              <div className="card-header-actions">
                {provider.required && <span className="badge">Required</span>}
                <span
                  className={`badge ${provider.configured ? 'badge-ok' : provider.hasKey ? 'badge-warn' : 'badge-unknown'} tip tip-left`}
                  data-tip={
                    provider.configured
                      ? 'A key is saved and the integration is switched on. Acquisition can run.'
                      : provider.hasKey
                        ? 'A key is saved but the integration is switched off. Turn it on below.'
                        : 'No key saved yet. Enter one below to enable company lookups.'
                  }
                >
                  <span className="badge-dot" />
                  {provider.configured ? 'Ready' : provider.hasKey ? 'Disabled' : 'Not configured'}
                </span>
              </div>
            </div>

            <div className="card-body col" style={{ gap: 'var(--sp-5)' }}>
              <p className="text-sm text-dim" style={{ maxWidth: '640px' }}>
                {provider.description}
              </p>

              <form className="col" style={{ gap: 'var(--sp-4)', maxWidth: '640px' }} onSubmit={handleSave}>
                <div className="field">
                  <label className="field-label" htmlFor="ch-api-key">
                    API key
                    <button
                      type="button"
                      className="info-tip tip tip-below"
                      data-tip={`Register free at ${provider.docsUrl}, create an application, then create a REST API key. Streaming and OAuth keys will not work here.`}
                      aria-label="Where to get a Companies House API key"
                    >
                      i
                    </button>
                  </label>

                  <div className="secret-field">
                    <input
                      id="ch-api-key"
                      className="input secret-input"
                      type={revealed ? 'text' : 'password'}
                      value={keyInput}
                      onChange={(event) => setKeyInput(event.target.value)}
                      placeholder={
                        provider.hasKey
                          ? `${provider.maskedKey} — saved. Enter a new key to replace it.`
                          : 'Paste your Companies House REST API key'
                      }
                      autoComplete="off"
                      spellCheck="false"
                      disabled={anyBusy || readOnly}
                    />
                    <button
                      type="button"
                      className="btn btn-ghost btn-icon btn-sm secret-field-toggle tip tip-left"
                      onClick={() => setRevealed((current) => !current)}
                      data-tip={revealed ? 'Hide the key' : 'Show what you typed'}
                      aria-label={revealed ? 'Hide the key' : 'Show the key'}
                      disabled={!keyInput}
                    >
                      {revealed ? <IconEyeOff size={14} /> : <IconEye size={14} />}
                    </button>
                  </div>

                  <span className="field-hint">
                    Get a free key from{' '}
                    <a href={provider.docsUrl} target="_blank" rel="noreferrer">
                      developer.company-information.service.gov.uk
                    </a>
                    . Stored server-side as <code>{provider.envVar}</code>.
                  </span>
                </div>

                <div className="row wrap">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!typedKey || anyBusy || readOnly}
                    title={typedKey ? undefined : 'Enter a key first'}
                  >
                    {busy === 'saving' && <span className="spinner" />}
                    {busy === 'saving' ? 'Saving' : 'Save key'}
                  </button>

                  <button
                    type="button"
                    className="btn tip tip-below"
                    onClick={handleTest}
                    disabled={anyBusy || (!typedKey && !provider.hasKey)}
                    data-tip={
                      typedKey
                        ? 'Makes one live call to Companies House with the key you have typed, without saving it.'
                        : 'Makes one live call to Companies House with the saved key. Costs one request against the rate limit.'
                    }
                  >
                    {busy === 'testing' ? <span className="spinner" /> : <IconPlug size={14} />}
                    {busy === 'testing'
                      ? 'Testing'
                      : typedKey
                        ? 'Test without saving'
                        : 'Test connection'}
                  </button>

                  <span className="spacer" />

                  {provider.hasKey && (
                    <button
                      type="button"
                      className="btn btn-ghost tip tip-left"
                      onClick={() => setConfirmRemove(true)}
                      disabled={anyBusy || readOnly}
                      data-tip="Delete the key from apps/api/.env"
                    >
                      <IconTrash size={14} />
                      Remove
                    </button>
                  )}
                </div>
              </form>

              {actionError && (
                <div className="alert alert-error" role="alert">
                  <span className="alert-icon">
                    <IconAlert />
                  </span>
                  <span className="alert-body">{actionError}</span>
                </div>
              )}

              {notice && !actionError && (
                <div className="alert alert-info" role="status">
                  <span className="alert-icon">
                    <IconCheck />
                  </span>
                  <span className="alert-body">{notice}</span>
                </div>
              )}

              {lastTest && (
                <TestResultAlert
                  result={lastTest.result}
                  testedStoredKey={lastTest.testedStoredKey}
                />
              )}

              {/* --- Switch, separate from the key itself ------------------
                  A key can be saved but held off, which is how you stop
                  outbound calls without throwing the credential away. */}
              <div className="col-tight">
                <label className="checkbox-row">
                  <input
                    type="checkbox"
                    className="checkbox"
                    checked={provider.enabled}
                    onChange={(event) => void handleToggleEnabled(event.target.checked)}
                    disabled={anyBusy || readOnly || (!provider.hasKey && !provider.enabled)}
                  />
                  Enable the Companies House integration
                  {busy === 'toggling' && <span className="spinner" />}
                </label>
                <span className="field-hint">
                  {provider.hasKey
                    ? `Written as ${provider.enabledVar} in apps/api/.env. With this off, the application makes no outbound calls to Companies House.`
                    : 'Save a key first — enabling the integration without one would only produce authentication failures.'}
                </span>
              </div>

              <div className="kv">
                <span className="kv-key">Saved key</span>
                <span className="kv-value mono">
                  {provider.hasKey ? provider.maskedKey : <span className="value-absent">none</span>}
                </span>

                <span className="kv-key">Environment variable</span>
                <span className="kv-value mono">{provider.envVar}</span>

                <span className="kv-key">
                  Rate limit
                  <button
                    type="button"
                    className="info-tip tip tip-below"
                    data-tip="Applies to the key, across the whole application. The acquisition engine throttles globally to stay inside it."
                    aria-label="What the rate limit means"
                  >
                    i
                  </button>
                </span>
                <span className="kv-value mono">{provider.rateLimit}</span>

                <span className="kv-key">Last tested</span>
                <span className="kv-value">
                  {provider.lastTest ? (
                    <span className={provider.lastTest.ok ? 'text-ok' : 'text-error'}>
                      {provider.lastTest.ok ? 'Passed' : 'Failed'} · {timeAgo(provider.lastTest.checkedAt)}
                    </span>
                  ) : (
                    <span className="value-absent">not tested since the API started</span>
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="alert alert-info" style={{ maxWidth: '760px' }}>
            <span className="alert-icon">
              <IconInfo />
            </span>
            <span className="alert-body">
              <span className="alert-title">Where the key lives</span>
              Saving writes <code>{provider.envVar}</code> and <code>{provider.enabledVar}</code>{' '}
              into <code>apps/api/.env</code>, keeping the comments around them intact, and the
              running API picks the change up on its next request. The previous file is kept as{' '}
              <code>.env.bak</code>. That file is gitignored — the key never enters version
              control, and it is never sent to the browser.
            </span>
          </div>
        </div>
      )}

      {confirmRemove && (
        <RemoveDialog
          busy={busy === 'removing'}
          onCancel={() => setConfirmRemove(false)}
          onConfirm={() => void handleRemove()}
        />
      )}
      </div>
    </div>
  );
}
